import type { ReportBlock } from './exception.model';
import type {
  PersistedCaseHeader,
  Uc1AgentCaseCandidate,
  Uc1AgentCaseEvidence,
  WellsExceptionPayload,
} from './persisted-case.model';

export enum WorkflowBadgeStatus {
  New = 'New',
  AwaitingDecision = 'AwaitingDecision',
  Closed = 'Closed',
  Proposed = 'Proposed',
}

export enum StepStatus {
  Done = 'done',
  Current = 'current',
  Pending = 'pending',
  Waiting = 'waiting',
}

export enum ActorType {
  System = 'system',
  AIAgent = 'ai agent',
  Human = 'human',
}

export enum WorkflowTab {
  All = 'All',
  New = 'New',
  AwaitingDecision = 'AwaitingDecision',
  Closed = 'Closed',
}

export enum WorkflowDecisionAction {
  Approve = 'Approve',
  Reject = 'Reject',
}

export interface WorkflowStep {
  number: number;
  title: string;
  actor: ActorType;
  status: StepStatus;
  sub: string;
  completedOn?: string | null;
  source?: string | null;
  completedBy?: string | null;
  completedByEmail?: string | null;
  action?: string | null;
  summary?: string | null;
  evidenceDataJson?: string | null;
  details?: string[];
  blocks?: ReportBlock[];
}

export interface WorkflowIdentity {
  title: string;
  area: string;
  supplementary: string;
  stream: string;
  product: string;
  exceptionType: string;
  useCase: string;
  confidenceLevel: number;
  confidenceStatement: string;
}

export interface WorkflowComment {
  id: string;
  text: string;
  author: string;
  authorEmail?: string | null;
  createdAt: string;
}

export type WorkflowStatus = 'new' | 'in-progress' | 'done';

export interface WorkflowItem {
  source?: 'workflow' | 'uc2-case';
  id: string;
  title: string;
  badge: WorkflowBadgeStatus;
  /** Latest engine-confirmed workflow state; absent when no workflow state is available. */
  workflowBadge?: WorkflowBadgeStatus;
  workflowStatus?: WorkflowStatus;
  workflowStepTitle?: string | null;
  workflowOutcome?: string | null;
  /** Current open step owner from the engine; undefined when ownership could not be read. */
  workflowOwnerOid?: string | null;
  asset?: string;
  priority?: string | null;
  confidenceScore?: number | null;
  confidenceBand?: string | null;
  description: string;
  createdDate?: string | null;
  updatedDate?: string | null;
  /** Completion of the run that produced the currently applied case version, in UTC. */
  runCompletedAt?: string | null;
  /** Latest analysis sweep timestamp supplied by the AgentCase header, in UTC. */
  sweepTimeUtc?: string | null;
  runForDate?: string | null;
  /** The day the underlying data represents (payload dataForDate), independent of the card's run-for date. */
  dataForDate?: string | null;
  /** Payload displayReport flag; "hide" suppresses the report from the inbox. */
  displayReport?: string | null;
  exceptionReferenceId?: string | null;
  assignedTo?: string | null;
  status: string;
  actionText?: string;
  summary?: string;
  identity?: WorkflowIdentity;
  steps: WorkflowStep[];
  comments?: WorkflowComment[];
  agentFeedback?: string | null;
  persistedCase?: PersistedCaseHeader;
  casePayload?: WellsExceptionPayload;
  caseDetailError?: string;
  /** Present for UC1 schema 2 AgentCases, whose ranked candidates replace legacy workflow hypotheses. */
  uc1Candidates?: Uc1AgentCaseCandidate[];
  /** Root-level UC1 schema 2 evidence records referenced by candidates and the summary. */
  uc1Evidence?: Uc1AgentCaseEvidence[];
  /** Evidence references associated with the UC1 schema 2 case summary. */
  uc1SummaryEvidenceIds?: string[];
  /** Set when the case is driven by the workflow engine; the engine owns its step, owner, and audit. */
  engineCaseId?: string | null;
  /** Set on a legacy record whose Dossier opened an engine case; the detail shows that case in its workflow tab. */
  linkedEngineCaseId?: string | null;
  /** The engine case intake opened or joined for this record (API `workflowCaseId`); null until intake links one. */
  workflowCaseId?: string | null;
  /** The engine's readable case number for `workflowCaseId`, for example `IONMA-PD-000001`. */
  workflowCaseNumber?: string | null;
}

export interface Facility {
  id: string;
  name: string;
}
